class FFCollectionCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.ff-collection-carousel__viewport');
    this.track = this.querySelector('[data-carousel-track]');
    this.clone = this.track?.querySelector('.ff-collection-carousel__track-clone');
    if (!this.viewport || !this.track) return;

    this.manualMode = false;
    this.resumeTimer = null;

    this.bindHover();
    this.bindDragScroll();
    this.bindWheelScroll();
    this.bindTouchScroll();
    this.bindResize();

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.classList.toggle('is-paused', !entry.isIntersecting);
          });
        },
        { threshold: 0 }
      );
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
  }

  getMaxScroll() {
    return Math.max(0, this.track.scrollWidth - this.viewport.clientWidth);
  }

  clampScroll() {
    const max = this.getMaxScroll();
    if (this.viewport.scrollLeft < 0) {
      this.viewport.scrollLeft = 0;
    } else if (this.viewport.scrollLeft > max) {
      this.viewport.scrollLeft = max;
    }
  }

  getTrackOffset() {
    const transform = getComputedStyle(this.track).transform;
    if (!transform || transform === 'none') return 0;
    const matrix = transform.match(/matrix\(([^)]+)\)/);
    if (matrix) return Math.abs(parseFloat(matrix[1].split(',')[4]) || 0);
    const matrix3d = transform.match(/matrix3d\(([^)]+)\)/);
    if (matrix3d) return Math.abs(parseFloat(matrix3d[1].split(',')[12]) || 0);
    return 0;
  }

  enterManualMode() {
    if (this.manualMode) {
      this.clampScroll();
      return;
    }

    const offset = this.getTrackOffset();
    this.manualMode = true;
    this.classList.add('is-paused', 'is-manual');
    this.viewport.classList.add('is-manual');

    if (this.clone) this.clone.style.display = 'none';

    const maxScroll = this.getMaxScroll();
    this.viewport.scrollLeft = Math.min(offset, maxScroll);
  }

  scheduleAutoResume(delay = 1200) {
    clearTimeout(this.resumeTimer);
    this.resumeTimer = window.setTimeout(() => {
      if (!this.matches(':hover')) {
        this.exitManualMode();
      }
    }, delay);
  }

  exitManualMode() {
    if (!this.manualMode) return;
    this.manualMode = false;
    this.viewport.classList.remove('is-manual', 'is-dragging');
    this.viewport.scrollLeft = 0;
    this.classList.remove('is-paused', 'is-manual');

    if (this.clone) this.clone.style.display = 'contents';
  }

  bindResize() {
    window.addEventListener('resize', () => {
      if (this.manualMode) this.clampScroll();
    }, { passive: true });
  }

  bindHover() {
    this.addEventListener('mouseenter', () => {
      clearTimeout(this.resumeTimer);
    });

    this.addEventListener('mouseleave', () => {
      this.viewport.classList.remove('is-dragging');
      this.exitManualMode();
    });
  }

  bindDragScroll() {
    const DRAG_THRESHOLD = 6;
    let isDragging = false;
    let modeEntered = false;
    let startX = 0;
    let scrollLeft = 0;
    let dragDistance = 0;

    this.viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      isDragging = true;
      modeEntered = false;
      dragDistance = 0;
      startX = event.clientX;
      scrollLeft = this.viewport.scrollLeft;
      this.pointerId = event.pointerId;
    });

    this.viewport.addEventListener('pointermove', (event) => {
      if (!isDragging) return;
      const walk = event.clientX - startX;
      dragDistance = Math.max(dragDistance, Math.abs(walk));

      if (!modeEntered) {
        if (dragDistance <= DRAG_THRESHOLD) return;
        modeEntered = true;
        this.enterManualMode();
        scrollLeft = this.viewport.scrollLeft;
        this.viewport.classList.add('is-dragging');
        this.viewport.setPointerCapture?.(this.pointerId);
      }

      this.viewport.scrollLeft = scrollLeft - walk;
      this.clampScroll();
    });

    const endDrag = (event) => {
      if (!isDragging) return;
      isDragging = false;
      this.viewport.classList.remove('is-dragging');
      if (modeEntered) {
        this.viewport.releasePointerCapture?.(event.pointerId);
        this.clampScroll();
        this.scheduleAutoResume(1500);
      }
    };

    this.viewport.addEventListener('pointerup', endDrag);
    this.viewport.addEventListener('pointercancel', endDrag);

    this.viewport.addEventListener(
      'click',
      (event) => {
        if (dragDistance > 6) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  bindWheelScroll() {
    this.viewport.addEventListener(
      'wheel',
      (event) => {
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX) && Math.abs(event.deltaX) < 2) return;

        this.enterManualMode();

        const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        this.viewport.scrollLeft += delta;
        this.clampScroll();
        this.scheduleAutoResume(1500);
        event.preventDefault();
      },
      { passive: false }
    );
  }

  bindTouchScroll() {
    const SWIPE_THRESHOLD = 6;
    let startX = 0;
    let startY = 0;
    let modeEntered = false;

    this.viewport.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        modeEntered = false;
      },
      { passive: true }
    );

    this.viewport.addEventListener(
      'touchmove',
      (event) => {
        if (modeEntered) return;
        const touch = event.touches[0];
        const deltaX = Math.abs(touch.clientX - startX);
        const deltaY = Math.abs(touch.clientY - startY);
        if (deltaX <= SWIPE_THRESHOLD || deltaX <= deltaY) return;

        modeEntered = true;
        this.enterManualMode();
      },
      { passive: true }
    );

    this.viewport.addEventListener(
      'scroll',
      () => {
        if (this.manualMode) this.clampScroll();
      },
      { passive: true }
    );

    this.viewport.addEventListener(
      'touchend',
      () => {
        if (!modeEntered) return;
        this.clampScroll();
        this.scheduleAutoResume(2000);
      },
      { passive: true }
    );
  }
}

if (!customElements.get('ff-collection-carousel')) {
  customElements.define('ff-collection-carousel', FFCollectionCarousel);
}
